import React from "react";
import "../organization-management/organization-management.css";

/**
 * Tenant workflow: onboard a B2B customer onto the portal.
 *
 * Sidebar / menu: add a leaf via API `global/get-menus` with
 *   content: "CustomerOnboarding"
 *   path: "/customer-onboarding" (or your routing convention)
 * Capture of actual field values is done in Organization master → Customer master (embedded).
 */
export default function CustomerOnboarding() {
  const steps = [
    {
      title: "Register organization context",
      body:
        "Ensure the selling / servicing organization exists in Organization master and hierarchy. The customer must map to the correct org node for pricing, ownership, and reporting.",
    },
    {
      title: "Create or link customer master",
      body:
        "In Organization master, open Customer master (embedded). Enter customer code, name, type, and required organization mapping (bill-to/ship-to and contacts as needed).",
    },
    {
      title: "Customer grouping & pricing driver",
      body:
        "Set customer group — used as a pricing driver together with customer-specific and org-level rules. Review read-only pricing resolution order on the same form.",
    },
    {
      title: "Portal login enablement",
      body:
        "Turn on Portal login enabled when the customer should access the partner portal. Leave off until credentials and policies are ready.",
    },
    {
      title: "Order creation & eligibility",
      body:
        "Enable Order creation allowed when the account may create sales orders. Align with credit, compliance, and commercial sign-off.",
    },
    {
      title: "Finalize & audit",
      body:
        "Confirm payment terms, credit limit, pricing basis, and default warehouse. Save organization; keep change history when APIs are connected.",
    },
  ];

  const attributeRows = [
    ["Customer ID", "System-generated unique identifier."],
    ["Customer code", "Unique business code."],
    ["Customer name", "Legal or trading name."],
    ["Customer short name", "Abbreviated name."],
    ["Tenant ID", "Root tenant linkage (read-only in UI)."],
    ["Organization ID", "Owning organization node (mapping)."],
    ["Parent customer ID", "Optional parent–child relation for corporate groups."],
    ["Customer type", "Retailer, distributor, wholesaler, marketplace, internal, other."],
    ["Customer group", "Pricing and commercial grouping driver."],
    ["Customer segment", "Business segmentation."],
    ["Sales channel", "General trade, modern trade, ecommerce, export, etc."],
    ["Market / region", "Geography or service region."],
    ["Billing name", "Billing entity name."],
    ["Billing / shipping address", "Address blocks for invoicing and fulfillment."],
    ["Tax registration number", "GST / VAT / tax number."],
    ["PAN / registration ref", "Optional regulatory identifier."],
    ["Currency", "Default order currency."],
    ["Payment terms", "Commercial payment term."],
    ["Credit limit", "Optional commercial control."],
    ["Price list ID", "Optional direct price list linkage."],
    ["Pricing basis", "Customer group, customer specific, contract based, or hybrid."],
    ["Portal login allowed", "Whether the customer can log in."],
    ["Portal username / email", "Login identity."],
    ["Portal user role", "Customer user profile."],
    ["Order creation allowed", "Whether the customer can create sales orders."],
    ["Default warehouse", "Optional fulfillment warehouse."],
    ["Sales owner (user ID)", "Account owner mapping."],
    ["Primary contact", "Name, email, phone."],
    ["Additional contacts", "Multi-contact rows."],
    ["Customer status", "Draft, active, inactive, blocked."],
    ["Effective from / to", "Activation and end dates."],
    ["Remarks / notes", "Free text."],
    ["Created by / on, Updated by / on", "Audit fields (set on save in UI)."],
  ];

  return (
    <div className="org-mgmt-page container-fluid px-2 px-md-3 py-3">
      <div className="org-mgmt-hero rounded-3 p-3 p-md-4 mb-4 shadow-sm">
        <div className="d-flex flex-wrap align-items-start justify-content-between gap-3">
          <div>
            <h4 className="fw-semibold mb-1 d-flex align-items-center gap-2">
              <i className="bi bi-person-plus text-primary" aria-hidden />
              Customer onboarding
            </h4>
            <p className="text-muted mb-0 small" style={{ maxWidth: "46rem" }}>
              End-to-end checklist for bringing a customer onto the portal. Operational data entry happens in{" "}
              <strong>Organization master</strong> (customer block); this page is the process guide and reference.
            </p>
          </div>
        </div>
      </div>

      <div className="row g-4">
        <div className="col-lg-7">
          <div className="card org-section-card h-100">
            <div className="card-header py-2 px-3 small fw-semibold text-body-secondary">Onboarding steps</div>
            <ol className="list-group list-group-flush list-group-numbered">
              {steps.map((s, i) => (
                <li key={i} className="list-group-item d-flex gap-3 py-3">
                  <div className="ms-2 flex-grow-1">
                    <div className="fw-semibold small mb-1">{s.title}</div>
                    <p className="mb-0 small text-muted">{s.body}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </div>
        <div className="col-lg-5">
          <div className="card org-section-card mb-4">
            <div className="card-header py-2 px-3 small fw-semibold text-body-secondary">Recommended attributes (reference)</div>
            <div className="table-responsive">
              <table className="table table-sm table-borderless mb-0 small">
                <thead>
                  <tr className="text-body-secondary">
                    <th scope="col" className="ps-3">
                      Attribute
                    </th>
                    <th scope="col">Description</th>
                  </tr>
                </thead>
                <tbody>
                  {attributeRows.map(([attr, desc], i) => (
                    <tr key={i}>
                      <td className="ps-3 text-nowrap fw-medium">{attr}</td>
                      <td className="text-muted">{desc}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="card-body border-top py-2 small text-muted">
              Enter values in <strong>Organization master</strong> → <strong>Customer master (embedded)</strong>.
            </div>
          </div>
          <div className="card org-section-card border-primary border-opacity-25">
            <div className="card-body py-3">
              <div className="fw-semibold small mb-2">
                <i className="bi bi-info-circle me-1 text-primary" aria-hidden />
                Menu wiring
              </div>
              <p className="small text-muted mb-0">
                This screen opens when the role menu from <code className="small">global/get-menus</code> includes an item
                with <code className="small">content: &quot;CustomerOnboarding&quot;</code>. Add it next to Organization
                master under the same parent (for example Data setup) so admins can open the guide alongside master data
                entry.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
